/**
 * Tag keys applied to agent conversations by the flag-cleanup and
 * auto-investigation agents, then read across the flag-cleanup UI (both in
 * `@gonfalon/vega` and its consumers in `static/ld`) to derive a
 * conversation's status.
 *
 * The GraphQL `tags` field types keys/values as plain `string`, so these
 * literals are not otherwise type-checked. Centralizing them here gives the
 * strings a single source of truth so a rename or typo only has to be fixed
 * in one place.
 *
 * Note: CHAT vs RUN list filtering uses GraphQL `origin` / `agent_kind`,
 * not a dedicated factory-analyze tag.
 */
export const CONVERSATION_TAG_KEYS = {
  /** Set when a cleanup run starts; the value is the flag key being cleaned up. */
  flagCleanupInProgress: 'flag-cleanup-in-progress',
  /** Set when a cleanup was started by the automated cleanup scheduler. */
  automatedFlagCleanup: 'automated-flag-cleanup',
  /** Set when a conversation was started automatically by an auto-investigation. */
  autoInvestigation: 'auto-investigation',
  /** A pull request was opened for the cleanup; the value is the PR URL. */
  prFiled: 'pr-filed',
  /** The cleanup PR was merged. */
  prMerged: 'pr-merged',
  /** The cleanup PR was closed without merging. */
  prClosed: 'pr-closed',
  /** The flag is still actively evaluated, so no cleanup was performed. */
  flagStillActive: 'flag-still-active',
  /** The flag could not be found in the scanned code. */
  flagNotFound: 'flag-not-found',
  /** The flag is a permanent lever and should not be removed. */
  flagPermanentLever: 'flag-permanent-lever',
  /** The agent needs clarification from the user before it can continue. */
  flagClarificationNeeded: 'flag-clarification-needed',
  /** A technical error prevented the cleanup from completing. */
  flagTechnicalError: 'flag-technical-error',
  sdkSetup: 'sdk-setup',
} as const;

export type ConversationTagKey = (typeof CONVERSATION_TAG_KEYS)[keyof typeof CONVERSATION_TAG_KEYS];
